<template>
  <div
    v-if="links"
    v-editable="links"
    :class="{ 'mt-4': hasMarginTop, 'w-full': fullWidth }"
    class="flex gap-2 overflow-y-auto scrollbar-hide sm:justify-center"
  >
    <Intersect
      v-for="(link, index) in links"
      :key="link._uid"
      :threshold="0.5"
      @enter.once="handleVisibility(link, index, $route)"
    >
      <NuxtLink
        event=""
        :to="toLocalePath(link.cta_url?.cached_url || '')"
        class="text-base font-normal leading-5 text-white underline"
        data-test-id="scrollable-link"
        @click="clickObserver(link, index, $route)"
      >
        {{ link.label }}
      </NuxtLink>
    </Intersect>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import type { Category } from '@scayle/storefront-nuxt'
import { getAllCategoryPaths } from '#layers/base/utils/category'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import { useRouter } from '#app/composables/router'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { getPageCategoryForTracking } from '~/composables/tracking/helpers'
import useRootCategories from '#layers/base/composables/useRootCategories'
import type { SbBannerLink } from '~/cms/storyblok/types/storyblok.d.ts'
import type { RouteLocation, RouteLocationNormalized } from '#vue-router'

export default defineComponent({
  name: 'ScrollableLinkList',
  props: {
    links: {
      type: Array as PropType<SbBannerLink[] | undefined>,
      required: true,
    },
    hasMarginTop: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    fullWidth: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  emits: ['view:promotion-intercept', 'click:promotion-click'],

  async setup(_, { emit }) {
    const { trackPromotionSelect, trackPromotionView } = useTrackingEvents()
    const router = useRouter()
    const { toLocalePath } = useRouteHelpers()
    const { data: rootCategories } = useRootCategories()

    return {
      handleVisibility: (
        link: SbBannerLink,
        index: number,
        route: RouteLocationNormalized,
      ) => {
        const url = toLocalePath(link.cta_url?.cached_url || '')
        emit('view:promotion-intercept', {
          clickPathCategory: getPageCategoryForTracking(
            route,
            getAllCategoryPaths(rootCategories.value?.categories as Category[]),
          ),
          url,
        })
        if (link.promotion_id) {
          trackPromotionView({
            id: link.promotion_id || 'scrollable_link_list_promotion_id',
            name: link.promotion_name || 'scrollable_link_list_promotion_name',
            promoType: 'scrollablelinklist',
            position: index + 1,
            creativeNameSb: link.creative_name || '',
          })
        }
      },
      clickObserver: (
        link: SbBannerLink,
        index: number,
        route: RouteLocation,
      ) => {
        const url = toLocalePath(link.cta_url?.cached_url || '')
        emit('click:promotion-click', {
          clickPathCategory: getPageCategoryForTracking(
            route,
            getAllCategoryPaths(rootCategories.value?.categories as Category[]),
          ),
          url,
        })
        router.push(toLocalePath(link.cta_url?.cached_url || ''))
        if (link.promotion_id) {
          trackPromotionSelect({
            id: link.promotion_id || 'scrollable_link_list_promotion_id',
            name: link.promotion_name || 'scrollable_link_list_promotion_name',
            promoType: 'scrollablelinklist',
            position: index + 1,
            creativeNameSb: link.creative_name || '',
          })
        }
      },
      toLocalePath,
    }
  },
})
</script>
